import React from 'react'
import { Col, Container, Form, Modal as BootstrapModal, Row } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { TemplateCloneRequest, TemplateLookup } from '../../../models/TemplateLibrary'
import { TemplateTypeBadge } from './TemplateTypeBadge'
import { useZocksAuth } from '../../../components/ScopedBlock/hooks'
import { TrackEvent, useTracking } from '../../../apps/professional-app/fa/common/tracking'
import { getTemplateLibraryTrackingDetails } from './tracking'
import { useAppSelector } from '../../../store/hooks'
import { selectSafeAuthenticatedUser } from '../../../store/auth/authSlice'
import { InputParameterHierarchyTypeDisplayName, InputParameterHierarchyType } from '../../../components/InputParameterHierarchy/model'
import type { TemplateOrigin } from './templateOrigin'
import { computeDefaultDuplicateScope } from './templateDuplicateScope'

export interface TemplateDuplicateModalProps {
  show: boolean
  loading: boolean
  templateValue: TemplateLookup
  onDuplicate: (request: TemplateCloneRequest) => void
  onCancel: () => void
  origin?: TemplateOrigin
}

export const TemplateDuplicateModal: React.FC<TemplateDuplicateModalProps> = ({ show, loading, templateValue, onDuplicate, onCancel, origin }) => {
  const { tracked } = useTracking()
  const { hasScope } = useZocksAuth()
  const hasTemplateAdminScope = hasScope('**', 'tmpl_lib:admin', ['tmpl_lib:admin'])
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const userOwnsTemplate = 'createdBy' in templateValue && templateValue.createdBy === currentUserId
  const cameFromMeetingTemplateRow = Boolean(origin?.context.meetingTemplateId)
  const cameFromAccountDefaultColumn = cameFromMeetingTemplateRow && !origin?.context.userId
  const defaultScope: TemplateCloneRequest['scope'] = computeDefaultDuplicateScope({
    hasTemplateAdminScope,
    templateScope: templateValue.scope,
    originContext: origin?.context,
  })

  const { control, getValues } = useForm<TemplateCloneRequest>({
    defaultValues: {
      name: `Copy of ${templateValue.name}`,
      description: templateValue.description,
      scope: defaultScope,
    } as TemplateCloneRequest,
  })
  const selectedScope = useWatch({ control, name: 'scope', defaultValue: defaultScope })
  const showReplacementHint = cameFromMeetingTemplateRow && (hasTemplateAdminScope || !userOwnsTemplate)
  const templateTypeLabel = InputParameterHierarchyTypeDisplayName[origin?.type as InputParameterHierarchyType] ?? 'template'
  const replacementHintText =
    selectedScope === 'account'
      ? `The duplicate will replace the account default ${templateTypeLabel} template in ${origin?.meetingTemplateName}.`
      : `The duplicate will replace your ${templateTypeLabel} template in ${origin?.meetingTemplateName}.`

  const handleDuplicate = tracked(
    onDuplicate,
    {
      eventName: TrackEvent.TEMPLATE_DUPLICATED,
    },
    {
      condition: !!templateValue,
      eventDetailsExtractor: (request) => ({
        ...getTemplateLibraryTrackingDetails(templateValue!),
        targetScope: request.scope,
      }),
    },
  )

  const body = (
    <Container>
      <Row className='m-0'>
        <Col className='p-0'>
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <div>
                <Form.Label>Name</Form.Label>
                <Form.Control id={field.name} placeholder='Enter a name...' {...field} />
              </div>
            )}
          />
        </Col>
      </Row>
      <Row className='m-0 pt-4'>
        <Col className='p-0'>
          <Controller
            name='description'
            control={control}
            render={({ field }) => (
              <div>
                <Form.Label>Description</Form.Label>
                <Form.Control id={field.name} as='textarea' rows={3} placeholder='Enter a description...' {...field} />
              </div>
            )}
          />
        </Col>
      </Row>
      <Row className='m-0 pt-4'>
        <Col className='p-0'>
          <Form.Label>Type</Form.Label>
          <div className='d-flex justify-content-start'>
            <TemplateTypeBadge templateValue={templateValue} />
          </div>
        </Col>
      </Row>
      <Row className='m-0 pt-4'>
        <Col className='p-0'>
          {hasTemplateAdminScope ? (
            cameFromAccountDefaultColumn ? (
              <div>
                <Form.Label>Access</Form.Label>
                <AccountAccessItem />
              </div>
            ) : (
              <Controller
                name='scope'
                control={control}
                render={({ field }) => (
                  <div>
                    <Form.Label>Access</Form.Label>
                    <Form.Check inline label={<AccountAccessItem />} type='radio' id='account' {...field} value='account' checked={field.value === 'account'} />
                    <Form.Check inline label={<PersonalAccessItem />} type='radio' id='user' {...field} value='user' checked={field.value === 'user'} />
                  </div>
                )}
              />
            )
          ) : (
            <div>
              <Form.Label>Access</Form.Label>
              <PersonalAccessItem />
            </div>
          )}
        </Col>
      </Row>
      {showReplacementHint ? (
        <Row className='m-0 pt-4'>
          <Col className='p-0 d-flex align-items-center gap-2 paragraph-medium zocks-text-gray-700'>
            <i className='bi bi-info-square flex-shrink-0' />
            <span>{replacementHintText}</span>
          </Col>
        </Row>
      ) : null}
    </Container>
  )

  return (
    <Modal
      header={<BootstrapModal.Title>Duplicate template</BootstrapModal.Title>}
      body={body}
      show={show}
      onHide={onCancel}
      footerPrimaryActionTitle='Duplicate'
      footerPrimaryActionLoading={loading}
      footerSecondaryActionDisabled={loading}
      showFooterActions={true}
      centered
      onFooterPrimaryAction={() => handleDuplicate(getValues())}
      onFooterSecondaryAction={onCancel}
      headerBodySeparator={true}
    />
  )
}

const PersonalAccessItem: React.FC = () => {
  return <AccessItem iconClassName='bi bi-person-fill' label='Personal' description='only you' />
}

const AccountAccessItem: React.FC = () => {
  return <AccessItem iconClassName='bi bi-people-fill' label='Account' description='anyone in your account' />
}

interface AccessItemProps {
  iconClassName: string
  label: string
  description: string
}

const AccessItem: React.FC<AccessItemProps> = ({ iconClassName, label, description }) => {
  return (
    <div className='d-flex gap-2 align-items-center'>
      <i className={`${iconClassName} flex-shrink-0`} />
      <div className='flex-shrink-0'>
        {label}
        <span className='zocks-text-gray-600'>{` (${description})`}</span>
      </div>
    </div>
  )
}
